import * as Yup from 'yup';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Unstable_Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CardHeader from '@mui/material/CardHeader';
// components
import FormProvider, { RHFTextField, RHFAutocomplete } from 'src/components/hook-form';
// types
import { VuonTrong } from 'src/types/vuontrong';
import { Box } from '@mui/system';
import { Button } from '@mui/material';
import { useRouter } from 'src/routes/hook';
import { paths } from 'src/routes/paths';
import { STATUS_OPTIONS } from 'src/constants/status';
import CustomLoadingButton from 'src/components/button/button-loadding-dark-model';
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

type Props = { current?: VuonTrong | null; onSubmitForm: (data: any) => Promise<void> | void };

interface FormValues {
  vuonTrong_ID : number;
  tenVuon: string;
  diaChi?: string;
  viTri?: string;
  ghiChu?: string;
  trangThai: number; // 1: Sử dụng, 2: Tạm ngưng, 3: Không sử dụng
}


export default function VuonTrongNewEditForm({ current, onSubmitForm }: Props) {
   const router = useRouter();
  const schema = Yup.object().shape({
    tenVuon: Yup.string().required('Tên vườn là bắt buộc'),
    diaChi: Yup.string().nullable(),
    viTri: Yup.string().nullable(),
    ghiChu: Yup.string().nullable(),
    trangThai: Yup.number().oneOf([1, 2, 3]).required('Trạng thái là bắt buộc'),
  });
  const handleClose = useCallback(() => {
    router.push(paths.dashboard.vuontrong.root);
  }, [router]);
  const defaultValues = useMemo<FormValues>(
    () => ({
      vuonTrong_ID: current?.vuonTrong_ID || 0,
      tenVuon: current?.tenVuon || '', 
      diaChi: current?.diaChi || '',
      viTri: current?.viTri || '',
      ghiChu: current?.ghiChu || '',
      trangThai: current?.trangThai ?? 1,
    }),
    [current]
  );

  const methods = useForm<FormValues>({ resolver: yupResolver(schema), defaultValues });
  const { handleSubmit, formState: { isSubmitting }, setValue, watch } = methods;

  const onSubmit = handleSubmit(async (data) => {
    await onSubmitForm({
      ...data,
      trangThai: data.trangThai,
    });
  });


  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid md={3} >
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Hướng dẫn
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Nhập thông tin vườn trồng.
          </Typography>
        </Grid>
        <Grid xs={12} md={9}>
          <Card>
            <CardHeader title="Thông tin vườn trồng" />
            <Stack spacing={3} sx={{ p: 3 }}>
              <RHFTextField name="tenVuon" label="Tên vườn *" />
              <RHFTextField name="diaChi" label="Địa chỉ" />
              <RHFTextField name="viTri" label="Vị trí" />
              <RHFTextField name="ghiChu" label="Ghi chú" multiline rows={3} />
              <RHFAutocomplete
                name="trangThai"
                label="Trạng thái"
                options={STATUS_OPTIONS}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.label}
                isOptionEqualToValue={(opt, val) => (typeof val === 'number' ? opt.value === val : opt.value === val.value)}
                value={STATUS_OPTIONS.find((o) => o.value === watch('trangThai')) || null}
                onChange={(e, newValue) => setValue('trangThai', newValue ? (newValue as any).value : 1, { shouldValidate: true })}
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>{option.label}</li>
                )}
              />

            </Stack>
          </Card>
           <Grid xs={12} md={8} sx={{ display: 'flex', alignItems: 'center', mt: 2, pt: 2, }}>
                  <Button color="inherit" variant="outlined" size="large" onClick={handleClose}>
                    Hủy
                  </Button>
          
                  <CustomLoadingButton
                    type="submit"
                    variant="contained"
                    size="large"
                    loading={isSubmitting}
                    sx={{ ml: 2 }}
                  >
                     {current ? 'Cập nhật' : 'Tạo mới'}
                  </CustomLoadingButton>
                </Grid> 
        </Grid>
      </Grid>
    </FormProvider>
  );
}


